import { useCallback, useRef } from "react";
import useContactBook from "../contacts/useContactBook";
import useContactWrites from "../contacts/useContactWrites";
import { buildCrmStatusUpdate } from "../../@types/sharedSchema";
import { optimisticCrmRowPatch } from "./crmStatusPatch";
import type { CrmStatus } from "../../@types/sharedSchema";
import type { CrmContactRow } from "../../@types/webotServer";

/**
 * Inline CRM writes from the list: status, sub-status and the 🔥 flag.
 *
 * WHY THIS DOES NOT USE `useLegacyContactWrites().updateContact` — this is the landmine of the whole
 * page. That function looks the contact up in `useLegacyContactsBook().allContacts`, which is the
 * 25,000-row in-browser array this page exists to stop loading. With the array empty it
 * takes its create branch and passes `existing = undefined` into the CRM normalizer, so
 * the derived legacy `customerSeriousness` mirror is computed from a null prior state.
 * Every inline edit made from this page would quietly corrupt that field.
 *
 * The write goes through the SERVER (`useContactWrites().updateContact` →
 * `POST /contacts/update`). It used to be a browser-direct `set(merge: true)` that carried
 * a client-computed `customerSeriousness`; the server now derives that mirror from the
 * stored document, mirrors the edit into Postgres before it answers, and refuses a contact
 * that is not in the caller's own book instead of minting one.
 *
 * `buildCrmStatusUpdate` is still called HERE, with the row we already hold as `existing`:
 * it is what keeps the three axes coherent on screen (a sub-status implies בטיפול, another
 * status clears the sub), and the optimistic repaint is built from its result.
 */

export type CrmStatusPatch = {
  crmStatus?: CrmStatus | null;
  crmSubStatus?: string | null;
  crmIsHot?: boolean | null;
};

export type CrmMutationsOptions = {
  /** Patch the row in the local list immediately, before the write lands. */
  patchContact: (ownerUserId: string, contactId: string, patch: Partial<CrmContactRow>) => void;
  onError?: (error: Error, retry: () => void) => void;
};

const sameUser = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default function useCrmMutations({ patchContact, onError }: CrmMutationsOptions) {
  const { updateContact } = useContactWrites();
  // The caller's OWN BOOK — the email for a personal book, the office's `org:` key for a
  // member of a pooled agency (plan D4), whose rows carry that key as their owner. The lock
  // below compares against the book, because that is what "my own" means.
  const { ownBookKey: myBookKey } = useContactBook();

  /**
   * Doc ids this tab has just written, with a timestamp. The realtime doorbell
   * consults it so our own echo does not produce a "3 contacts changed" pill.
   */
  const ownWritesRef = useRef<Map<string, number>>(new Map());

  const markOwnWrite = useCallback((contactId: string) => {
    ownWritesRef.current.set(contactId, Date.now());
    // Cheap sweep — the map only ever holds what one session has edited.
    ownWritesRef.current.forEach((at, id) => {
      if (Date.now() - at > 8000) ownWritesRef.current.delete(id);
    });
  }, []);

  const isOwnRecentWrite = useCallback(
    (contactId: string): boolean => {
      const at = ownWritesRef.current.get(contactId);
      return at !== undefined && Date.now() - at < 8000;
    },
    []
  );

  const applyStatus = useCallback(
    async (row: CrmContactRow, patch: CrmStatusPatch): Promise<void> => {
      // Keep the axes coherent exactly as the server chokepoint does: a sub-status
      // implies `in_progress`, any other status clears the sub, and the legacy
      // `customerSeriousness` mirror is recomputed from the MERGED state — which is
      // why the current row is passed as `existing`.
      const update = buildCrmStatusUpdate(patch, {
        crmStatus: row.crmStatus as CrmStatus | null,
        crmSubStatus: row.crmSubStatus,
        crmIsHot: row.crmIsHot
      });

      const previous: Partial<CrmContactRow> = {
        crmStatus: row.crmStatus,
        crmSubStatus: row.crmSubStatus,
        crmIsHot: row.crmIsHot,
        customerSeriousness: row.customerSeriousness
      };

      // Optimistic: the chip repaints before the round trip. A status click that
      // waits on the network feels broken at 15 taps a session.
      //
      // SPARSE, exactly like the write — `optimisticCrmRowPatch` mirrors the keys
      // `buildCrmStatusUpdate` actually returned. The dense form this replaced turned
      // every absent key into an explicit false/null on the row: setting a status
      // visually wiped the 🔥, toggling the 🔥 visually wiped the status, and the row
      // disagreed with its own document until something re-synced it. See the helper.
      patchContact(row.ownerUserId, row.contactId, optimisticCrmRowPatch(update));
      markOwnWrite(row.contactId);

      const run = async () => {
        // The caller's OWN book only. Every caller already gates on ownership before
        // drawing the control; this is the lock on the side where a miss would not be an
        // error but a write to the caller's own contact of the same number.
        if (!sameUser(row.ownerUserId, myBookKey)) {
          throw new Error(`Contact ${row.contactId} belongs to ${row.ownerUserId}, not to the signed-in user's book`);
        }
        // The canonical fields only: the server REJECTS the legacy mirror (it derives it),
        // and its hot flag is a plain boolean.
        const { customerSeriousness: _derivedByServer, ...fields } = update;
        if (fields.crmIsHot !== undefined) fields.crmIsHot = Boolean(fields.crmIsHot);
        await updateContact(row.contactId, fields);
      };

      try {
        await run();
      } catch (error) {
        // Put the row back the way the user last saw it, then offer a retry that
        // replays the same mutation. No refetch, no page-level error, no lost scroll.
        patchContact(row.ownerUserId, row.contactId, previous);
        onError?.(error as Error, () => {
          void applyStatus(row, patch);
        });
      }
    },
    [markOwnWrite, myBookKey, onError, patchContact, updateContact]
  );

  const setStatus = useCallback(
    (row: CrmContactRow, crmStatus: CrmStatus | null, crmSubStatus?: string | null) =>
      applyStatus(row, { crmStatus, crmSubStatus: crmSubStatus ?? null }),
    [applyStatus]
  );

  const toggleHot = useCallback((row: CrmContactRow) => applyStatus(row, { crmIsHot: !row.crmIsHot }), [applyStatus]);

  return { setStatus, toggleHot, isOwnRecentWrite };
}
